import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { getMangaBySlug, getMangaByCategory } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = getMangaBySlug(slug || '');

  if (!manga) {
    return (
      <>
        <SEOHead title="漫画未找到 - 妖精漫画" description="抱歉，您查找的漫画不存在。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您查找的漫画不存在或已被移除。</p>
            <Link to="/" className="inline-block px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedManga = getMangaByCategory(manga.category)
    .filter((m) => m.slug !== manga.slug)
    .slice(0, 6);

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 妖精漫画`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    dateModified: manga.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '妖精漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://qiyuah.cn/manga/${manga.slug}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${manga.category}漫画`, href: `/category/${getCategorySlug(manga.category)}` },
    { label: manga.title },
  ];

  function getCategorySlug(cat: string): string {
    const map: Record<string, string> = {
      '恋爱': 'romance',
      '热血': 'action',
      '奇幻': 'fantasy',
      '悬疑': 'mystery',
    };
    return map[cat] || 'all';
  }

  return (
    <>
      <SEOHead
        title={`${manga.title} - 妖精漫画`}
        description={`${manga.title} - ${manga.description.substring(0, 120)}`}
        keywords={`${manga.title},妖精漫画,韩国漫画,${manga.category}漫画,在线阅读`}
        ogType="article"
        canonicalUrl={`/manga/${manga.slug}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="px-4 md:px-6 py-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    className="w-full h-full object-cover object-top"
                    title={`${manga.title} - 妖精漫画免费韩国漫画`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{manga.author}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    manga.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manga.status}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-0.5"></i>{formatViews(manga.views)} 次阅读
                  </span>
                  <span className="text-sm text-accent-500 font-medium">★ {manga.rating}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
                </div>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/category/${getCategorySlug(manga.category)}`}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                章节列表 <span className="text-sm font-normal text-foreground-500 ml-1">（共 {manga.chapters.length} 话）</span>
              </h2>
              <div className="bg-background-100 rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200">
                  {manga.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="bg-background-50 py-3.5 px-4 flex items-center justify-between hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700 line-clamp-1">{chapter.title}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.updatedAt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {relatedManga.length > 0 && (
              <section className="mt-10">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
                  {relatedManga.map((m) => (
                    <MangaCard key={m.slug} manga={m} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}